<template>
  <div id="mapa-container">
    <Sidebar ref="sidebarRef" />
    <Notify ref="notifikace" />
    <Mista ref="mistaRef" />
    <UlozVlastniBod v-if="map" ref="UlozVlastniBodRef" :map="map" />
    <div id="dvMapaApp"></div>
  </div>
</template>

<script setup>
import Sidebar from "./Sidebar.vue";
import Notify from "./Notify.vue";
import Mista from "./Mista.vue";
import UlozVlastniBod from "./UlozVlastniBod.vue";

import { onMounted, onUnmounted, provide, ref, watch, nextTick } from "vue";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Top } from "../js/class/Top.js";
//import { MapaFce } from "../js/class/MapaFce.js";
import { LadzoConsole as Lc } from "../js/global/ladzo-console.js";

//import "long-press-event";

// Toast notifikace
const notifikace = ref(null);
provide("toast", notifikace);

// Mapa jako ref
const map = ref(null);
provide("map", map); // ✅ poskytnutí ref, ne value
/*
const filtryMista = ref({
  wc: false,
  elektrina: false,
});
*/

/*
const filtryMista = ref({
  zobrazMista: true,
  vkladatVlastniBody: false,
  zdroj: {
    park4night: true,
    vlastni: true,
  },
  vybava: {
    wc: false,
    elektrina: false,
    koupani: false,
    sprcha: false,
  },
  stitky: [],
});
*/
const storageFiltryKey = Top.getLocalStorageKey("filtryMista");

const defaultFiltry = {
  zobrazMista: true,
  vkladatVlastniBody: false,
  zdroj: {
    park4night: true,
    vlastni: true,
  },
  vybava: {
    wc: false,
    elektrina: false,
    koupani: false,
    sprcha: false,
  },
  stitky: [],
};

// 3️⃣ Načtení z localStorage nebo výchozí
let saved = null;
try {
  saved = JSON.parse(localStorage.getItem(storageFiltryKey));
} catch (e) {
  saved = null;
}

const filtryMista = ref(
  saved && typeof saved === "object" ? saved : defaultFiltry,
);

// 4️⃣ Uložení při změně
watch(
  filtryMista,
  (novyStav) => {
    localStorage.setItem(storageFiltryKey, JSON.stringify(novyStav));
  },
  { deep: true },
);

provide("filtryMista", filtryMista);

const mistaRef = ref(null);
// Provide funkce pro volání zvenku
provide("Mista.nactiMista", () => {
  mistaRef.value?.nactiMista?.(); // zavolá metodu v komponentě Mista, pokud existuje
});

const sidebarRef = ref(null);

const UlozVlastniBodRef = ref(null);

function parseCoordinatePart(part) {
  const normalized = String(part).trim().replace(",", ".");
  const match = normalized.match(/^([+-]?\d+(?:\.\d+)?)([NSEW])?$/i);

  if (!match) return null;

  const value = Number(match[1]);
  if (Number.isNaN(value)) return null;

  const direction = (match[2] || "").toUpperCase();
  if (direction === "S" || direction === "W") return -Math.abs(value);
  return value;
}

function parseCoordinateText(rawValue) {
  if (!rawValue) return null;

  const text = String(rawValue).trim();
  const parts = text.split(",");
  if (parts.length < 2) return null;

  const lat = parseCoordinatePart(parts[0]);
  const lng = parseCoordinatePart(parts[1]);

  if (lat === null || lng === null) return null;

  return L.latLng(lat, lng);
}

function getCoordinatesFromUrl() {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  return (
    parseCoordinateText(params.get("dopor")) ||
    parseCoordinateText(params.get("coords")) ||
    parseCoordinateText(params.get("souradnice"))
  );
}

function buildCoordinatePopupHtml(latlng, includeSaveButton = true) {
  const lat = latlng.lat.toFixed(6);
  const lng = latlng.lng.toFixed(6);
  const saveButtonId = includeSaveButton
    ? `ulozit-bod-${Date.now()}-${Math.random().toString(36).slice(2)}`
    : null;

  const popupHtml = `
    <div style="min-width:200px">
      <b>Souřadnice:</b><br/>
      ${lat}, ${lng}<br/><br/>
      Navigovat na:<br><br>
      <a href="https://mapy.com/fnc/v1/route?end=${lng},${lat}&routeType=car_fast&navigate=true" target="_blank" rel="noopener">📍 Mapy.cz</a><br/>
      <br><a href="https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}" target="_blank" rel="noopener">🌍 Google Maps</a><br/>
      <br><a href="https://waze.com/ul?ll=${lat},${lng}&navigate=yes" target="_blank" rel="noopener">🚗 Waze</a>
      ${
        includeSaveButton
          ? `<div style="margin-top:12px; padding-top:8px; border-top:1px solid #ddd;"><button type="button" id="${saveButtonId}" style="display:block; width:100%; padding:6px 10px; border:1px solid #0d6efd; background:#0d6efd; color:#fff; border-radius:4px; font-size:14px; cursor:pointer;">Uložit tento bod</button></div>`
          : ""
      }
    </div>
  `;

  return { popupHtml, saveButtonId, latlng: L.latLng(latlng) };
}

function openCoordinatePopup(latlng, includeSaveButton = true) {
  if (!map.value) return;

  const {
    popupHtml,
    saveButtonId,
    latlng: popupLatLng,
  } = buildCoordinatePopupHtml(latlng, includeSaveButton);

  L.popup().setLatLng(popupLatLng).setContent(popupHtml).openOn(map.value);

  if (includeSaveButton && saveButtonId) {
    window.setTimeout(() => {
      const saveButton = document.getElementById(saveButtonId);
      if (!saveButton) return;

      saveButton.onclick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        UlozVlastniBodRef.value?.otevri?.(popupLatLng, true);
      };
    }, 0);
  }
}

// Funkce pro výřez mapy
function getMapArea() {
  Lc.f("MapaApp - getMapArea");
  if (!map.value?.getBounds) return null;

  const bounds = map.value.getBounds();
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();

  const centerLat = (sw.lat + ne.lat) / 2;
  const centerLng = (sw.lng + ne.lng) / 2;
  const deltaLat = Math.abs(ne.lat - sw.lat);
  const deltaLng = Math.abs(ne.lng - sw.lng);

  return {
    jihZ: [sw.lat, sw.lng],
    sevV: [ne.lat, ne.lng],
    center: [centerLat, centerLng],
    deltaLat,
    deltaLng,
    zoom: map.value.getZoom(),
  };
}

provide("getMapArea", getMapArea); // ✅ poskytnutí funkce pro výřez

// Inicializace mapy
onMounted(() => {
  Lc.f("Leaflet mapa z Vue");
  Lc.log("Top", Top);

  /*
  //map.value = L.map("dvMapaApp").setView([49.8, 15.5], 8);
  map.value = L.map("dvMapaApp", {
    zoomAnimation: false,
  }).setView([49.8, 15.5], 8);

  map.value.on("click", (e) => {
    sidebarRef.value?.zavri();
    if (filtryMista.value.vkladatVlastniBody) {
      Lc.f("Kliknuto na -------------- mapu");
      sidebarRef.value?.zavri();
      UlozVlastniBodRef.value?.otevri(e.latlng);
    }
  });
  */

  const savedCenter = JSON.parse(
    localStorage.getItem(Top.getLocalStorageKey("mapCenter")) || "null",
  );
  const savedZoom = parseInt(
    localStorage.getItem(Top.getLocalStorageKey("mapZoom")),
    10,
  );

  const initialCenter = Array.isArray(savedCenter) ? savedCenter : [49.8, 15.5];
  const initialZoom = Number.isInteger(savedZoom) ? savedZoom : 8;
  /*
  const mapInstance = L.map("dvMapaApp", {
    zoomAnimation: false,
  }).setView([49.8, 15.5], 8);
  */

  const mapInstance = L.map("dvMapaApp", {
    zoomAnimation: false,
    tapHold: true, // 👈 přidej pokud podporuješ
  }).setView(initialCenter, initialZoom);

  map.value = mapInstance; // až PO inicializaci

  if (map.value.tapHold) {
    map.value.tapHold.enable(); // 👈 důležité!
  }

  /*
  map.value.on("contextmenu", (e) => {
    L.popup()
      .setLatLng(e.latlng)
      .setContent("Dlouhé podržení zachyceno 🎯")
      .openOn(map.value);
  });
  */
  map.value.on("contextmenu", (e) => {
    openCoordinatePopup(e.latlng, true);
  });
  mapInstance.on("click", (e) => {
    Lc.log("Kliknuto na mapu:", e.latlng);
    UlozVlastniBodRef.value?.zavri?.();
    sidebarRef.value?.zavri();
    if (filtryMista.value.vkladatVlastniBody) {
      Lc.f("Kliknuto na -------------- mapu");
      sidebarRef.value?.zavri();
      UlozVlastniBodRef.value?.otevri(e.latlng);
    }
  });

  /*
  MapaFce.onLongPress(map.value, (latlng) => {
    Lc.log("Dlouhé podržení na: " + latlng.lat + ", " + latlng.lng);
  });
  */

  /*
  MapaFce.onLongPress(map.value, (latlng) => {
    const lat = latlng.lat.toFixed(6);
    const lng = latlng.lng.toFixed(6);

    const popupHtml = `
    <div style="min-width:200px">
      <b>Souřadnice:</b><br/>
      ${lat}, ${lng}<br/><br/>
      <a href="https://mapy.cz/zakladni?x=${lng}&y=${lat}&z=15" target="_blank">📍 Mapy.cz</a><br/>
      <a href="https://www.google.com/maps?q=${lat},${lng}" target="_blank">🌍 Google Maps</a><br/>
      <a href="https://waze.com/ul?ll=${lat},${lng}&navigate=yes" target="_blank">🚗 Waze</a>
    </div>
  `;

    // Zpoždění popupu – vyhne se zavření kvůli touchend
    setTimeout(() => {
      L.popup().setLatLng(latlng).setContent(popupHtml).openOn(map.value);
    }, 50); // může být 10–100 ms
  });

  */

  //zmena mapy pres ikonu vrstev pamy
  mapInstance.on("baselayerchange", function (e) {
    const selectedMapName = e.name;
    let key = Top.getLocalStorageKey("selectedBaseMap");
    localStorage.setItem(key, selectedMapName);
  });

  //ukladani pozice mapy
  mapInstance.on("moveend", () => {
    const center = mapInstance.getCenter();
    const zoom = mapInstance.getZoom();

    localStorage.setItem(
      Top.getLocalStorageKey("mapCenter"),
      JSON.stringify([center.lat, center.lng]),
    );
    localStorage.setItem(Top.getLocalStorageKey("mapZoom"), zoom);
  });

  const API_KEY = Top.localhost
    ? Top.mapy_cz_api_key_localhost
    : Top.mapy_cz_api_key;
  Lc.log("API_KEY", API_KEY);

  let tileLayers = {
    "Základní mapa": L.tileLayer(
      `https://api.mapy.com/v1/maptiles/basic/256/{z}/{x}/{y}?apikey=${API_KEY}`,
      {
        minZoom: 0,
        maxZoom: 19,
        attribution:
          '<a href="https://api.mapy.com/copyright" target="_blank">&copy; Seznam.cz a.s. a další</a>',
      },
    ),
    "Turistická mapa": L.tileLayer(
      `https://api.mapy.com/v1/maptiles/outdoor/256/{z}/{x}/{y}?apikey=${API_KEY}`,
      {
        minZoom: 0,
        maxZoom: 19,
        attribution:
          '<a href="https://api.mapy.com/copyright" target="_blank">&copy; Seznam.cz a.s. a další</a>',
      },
    ),
    "Zimní mapa": L.tileLayer(
      `https://api.mapy.com/v1/maptiles/winter/256/{z}/{x}/{y}?apikey=${API_KEY}`,
      {
        minZoom: 0,
        maxZoom: 19,
        attribution:
          '<a href="https://api.mapy.com/copyright" target="_blank">&copy; Seznam.cz a.s. a další</a>',
      },
    ),
    "Letecká mapa": L.tileLayer(
      `https://api.mapy.com/v1/maptiles/aerial/256/{z}/{x}/{y}?apikey=${API_KEY}`,
      {
        minZoom: 0,
        maxZoom: 19,
        attribution:
          '<a href="https://api.mapy.com/copyright" target="_blank">&copy; Seznam.cz a.s. a další</a>',
      },
    ),
  };

  //
  tileLayers["OpenTopoMap (vrstevnice)"] = L.tileLayer(
    "https://tile.opentopomap.org/{z}/{x}/{y}.png",
    {
      minZoom: 0,
      maxZoom: 17,
      attribution: "© OpenTopoMap, © OpenStreetMap contributors",
    },
  );

  tileLayers["Satelitní mapa (Esri)"] = L.tileLayer(
    "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    {
      minZoom: 0,
      maxZoom: 19,
      attribution: "© Esri & contributors",
    },
  );

  tileLayers["Turistické trasy (Waymarked Trails)"] = L.tileLayer(
    "https://tile.waymarkedtrails.org/hiking/{z}/{x}/{y}.png",
    {
      minZoom: 0,
      maxZoom: 18,
      attribution: "© waymarkedtrails.org, © OpenStreetMap contributors",
      opacity: 0.7, // volitelně průhledné jako overlay
    },
  );

  /*
  tileLayers["Thunderforest Outdoor"] = L.tileLayer(
    "https://{s}.tile.thunderforest.com/outdoors/{z}/{x}/{y}.png?apikey=" +
      Top.thunderforest_api_key,
    {
      attribution: "© Thunderforest, © OpenStreetMap contributors",
      maxZoom: 22,
      subdomains: ["a", "b", "c"],
    }
  );
  */

  const thunderforestAttribution =
    "© Thunderforest, © OpenStreetMap contributors";
  const thunderforestOptions = {
    attribution: thunderforestAttribution,
    maxZoom: 22,
    subdomains: ["a", "b", "c"],
  };

  tileLayers["Thunderforest OpenCycleMap"] = L.tileLayer(
    "https://tile.thunderforest.com/cycle/{z}/{x}/{y}.png?apikey=" +
      Top.thunderforest_api_key,
    thunderforestOptions,
  );
  tileLayers["Thunderforest Transport"] = L.tileLayer(
    "https://tile.thunderforest.com/transport/{z}/{x}/{y}.png?apikey=" +
      Top.thunderforest_api_key,
    thunderforestOptions,
  );
  tileLayers["Landscape"] = L.tileLayer(
    "https://tile.thunderforest.com/landscape/{z}/{x}/{y}.png?apikey=" +
      Top.thunderforest_api_key,
    thunderforestOptions,
  );
  tileLayers["Thunderforest Outdoors"] = L.tileLayer(
    "https://tile.thunderforest.com/outdoors/{z}/{x}/{y}.png?apikey=" +
      Top.thunderforest_api_key,
    thunderforestOptions,
  );
  /*
  tileLayers["Thunderforest Transport Dark"] = L.tileLayer(
    "https://tile.thunderforest.com/transport-dark/{z}/{x}/{y}.png?apikey=" +
      Top.thunderforest_api_key,
    thunderforestOptions
  );
  tileLayers["Thunderforest Spinal Map"] = L.tileLayer(
    "https://tile.thunderforest.com/spinal-map/{z}/{x}/{y}.png?apikey=" +
      Top.thunderforest_api_key,
    thunderforestOptions
  );
  */
  tileLayers["Thunderforest Pioneer"] = L.tileLayer(
    "https://tile.thunderforest.com/pioneer/{z}/{x}/{y}.png?apikey=" +
      Top.thunderforest_api_key,
    thunderforestOptions,
  );
  tileLayers["Mobile Atlas"] = L.tileLayer(
    "https://tile.thunderforest.com/mobile-atlas/{z}/{x}/{y}.png?apikey=" +
      Top.thunderforest_api_key,
    thunderforestOptions,
  );
  tileLayers["Thunderforest Neighbourhood"] = L.tileLayer(
    "https://tile.thunderforest.com/neighbourhood/{z}/{x}/{y}.png?apikey=" +
      Top.thunderforest_api_key,
    thunderforestOptions,
  );
  tileLayers["Thunderforest Atlas"] = L.tileLayer(
    "https://tile.thunderforest.com/atlas/{z}/{x}/{y}.png?apikey=" +
      Top.thunderforest_api_key,
    thunderforestOptions,
  );

  tileLayers["Základní mapa (Carto Positron)"] = L.tileLayer(
    "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> & Carto',
      subdomains: "abcd",
      maxZoom: 20,
    },
  );

  tileLayers["OSM (standard)"] = L.tileLayer(
    "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
    },
  );
  //tileLayers["Turistická mapa"].addTo(mapInstance);
  //tileLayers["OpenTopoMap (vrstevnice)"].addTo(mapInstance);

  //nacteni mapy z localstorage nebo použití výchozí

  const savedMapName = localStorage.getItem(
    Top.getLocalStorageKey("selectedBaseMap"),
  );
  const defaultMapName =
    savedMapName && tileLayers[savedMapName] ? savedMapName : "OSM (standard)";
  tileLayers[defaultMapName].addTo(mapInstance);

  //tileLayers["OSM (standard)"].addTo(mapInstance);
  L.control.layers(tileLayers).addTo(mapInstance);

  const targetFromUrl = getCoordinatesFromUrl();
  if (targetFromUrl) {
    mapInstance.setView(targetFromUrl, 15);
    openCoordinatePopup(targetFromUrl, true);
  }
});
</script>

<style scoped>
#dvMapaApp {
  height: 100vh;
  height: 100vh;
  position: fixed !important;
  left: 0;
  top: 0;
  width: 100vw;
}
</style>
